<template>
  <div class="atlas-library-progress" role="status" :aria-label="ariaLabel">
    <div class="atlas-library-progress__header">
      <div class="atlas-library-progress__title-row">
        <span class="atlas-library-progress__icon-wrap atlas-library-progress__icon-wrap--spinning" aria-hidden="true">
          <IconEmbedded name="reload_2" :size="22" color="#fff" />
        </span>
        <span class="atlas-library-progress__title">Uploading...</span>
        <span class="atlas-library-progress__percent">{{ clampedPercent }}%</span>
      </div>
      <div class="atlas-library-progress__sub-row">
        <p class="atlas-library-progress__sub-text">
          Now uploading <strong>{{ currentGroupName }}</strong>
          <span v-if="currentGroupTotalCount > 0" class="atlas-library-progress__sub-sep">·</span>
          <span v-if="currentGroupTotalCount > 0">{{ currentGroupUploadedCount }} / {{ currentGroupTotalCount }} images</span>
        </p>
      </div>
    </div>
    <div class="atlas-library-progress__bar-track" aria-hidden="true">
      <div class="atlas-library-progress__bar-fill" :style="{ width: `${clampedPercent}%` }" />
    </div>
    <div class="atlas-library-progress__stats">
      <span class="atlas-library-progress__stat">
        <strong>{{ uploadedImagesCount.toLocaleString() }}</strong> / {{ totalImagesToUpload.toLocaleString() }} images
      </span>
      <span class="atlas-library-progress__stat">
        <strong>{{ completedGroupsCount }}</strong> / {{ totalGroupsCount }} groups complete
      </span>
      <span class="atlas-library-progress__stat">{{ etaLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

defineOptions({
  name: "AtlasIngestionLibraryProgress",
});

const props = defineProps<{
  overallPercent: number;
  currentGroupName: string;
  currentGroupUploadedCount: number;
  currentGroupTotalCount: number;
  uploadedImagesCount: number;
  totalImagesToUpload: number;
  completedGroupsCount: number;
  totalGroupsCount: number;
  secondsRemainingApprox: number | null;
}>();

const clampedPercent = computed(() => Math.min(100, Math.max(0, Math.round(props.overallPercent))));

const etaLabel = computed(() => {
  const s = props.secondsRemainingApprox;
  if (s == null || !Number.isFinite(s) || s < 0) {
    return "—";
  }
  if (s < 60) {
    return `~${Math.max(1, Math.round(s))}s remaining`;
  }
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `~${m}m ${sec}s remaining`;
});

const ariaLabel = computed(() => {
  return `Library upload ${clampedPercent.value} percent complete. ${props.uploadedImagesCount} of ${props.totalImagesToUpload} images.`;
});
</script>

<style scoped lang="scss">
$atlas-teal: #118689;

.atlas-library-progress {
  padding: 16px 18px 14px;
  border-radius: 12px;
  border: 1.5px solid rgba($atlas-teal, 0.55);
  background: rgba($atlas-teal, 0.06);
  box-sizing: border-box;
}

.atlas-library-progress__header {
  margin-bottom: 12px;
}

.atlas-library-progress__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.atlas-library-progress__icon-wrap {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: $atlas-teal;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

@keyframes atlas-library-progress-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Spin the wrapper, not the inner SVG wrap — one composited layer, less SVG repaint jank. */
.atlas-library-progress__icon-wrap--spinning {
  transform-origin: 50% 50%;
  backface-visibility: hidden;
  will-change: transform;
  animation: atlas-library-progress-spin 1.6s linear infinite;
}

.atlas-library-progress__title {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.3;
  color: #1a1a1a;
}

.atlas-library-progress__percent {
  flex-shrink: 0;
  margin-left: auto;
  font-weight: 700;
  font-size: 26px;
  line-height: 1.1;
  color: $atlas-teal;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.atlas-library-progress__sub-row {
  padding-left: 46px;
}

@media (max-width: 520px) {
  .atlas-library-progress__sub-row {
    padding-left: 0;
  }
}

.atlas-library-progress__sub-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--srp-color-text-muted, #555);
}

.atlas-library-progress__sub-sep {
  margin: 0 4px;
}

.atlas-library-progress__bar-track {
  height: 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 12px;
}

.atlas-library-progress__bar-fill {
  height: 100%;
  border-radius: 999px;
  background: $atlas-teal;
  transition: width 0.25s ease-out;
  min-width: 0;
}

.atlas-library-progress__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--srp-color-text-muted, #555);
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.atlas-library-progress__stat {
  white-space: nowrap;

  strong {
    color: #1a1a1a;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum";
  }
}
</style>
